import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertTriangle, Database, User, Calendar,
  Package, FileText, Hash, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { numeroGestionale } from '../../utils/numeroDocumento';
import AggiornaDalGestionale from '../../components/admin/AggiornaDalGestionale';

function fmt(n: number | string | undefined | null) {
  if (n == null || n === '') return '—';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(num)) return '—';
  return `€ ${num.toFixed(2).replace('.', ',')}`;
}
function fmtDate(iso: string | undefined | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return String(iso).slice(0, 10) || '—'; }
}
function fmtNum(n: any, decimals = 2) {
  if (n == null || n === '') return '—';
  const num = typeof n === 'string' ? parseFloat(n) : Number(n);
  if (isNaN(num)) return String(n);
  return num.toFixed(decimals).replace('.', ',');
}

interface ErpOrderRiga {
  // NTS Business-API riga (collection in RigheOrd.Collection)
  ec_riga?: number;
  ec_codart?: string;
  ec_descr?: string;
  ec_quant?: number;
  ec_quaeva?: number;
  ec_prezzo?: number;
  ec_valore?: number;
  ec_unmis?: string;
  Campi?: {
    ec_scont1?: number;
    ec_scont2?: number;
    ec_scont3?: number;
    ec_note?: string | null;
    [k: string]: any;
  };
  [key: string]: any;
}

interface ErpOrderDetail {
  et_tipork?: string;
  et_anno?: number;
  et_serie?: string;
  et_numdoc?: number;
  et_conto?: number;
  et_datdoc?: string;
  et_totdoc?: number;
  et_aperto?: string;
  et_confermato?: string;
  Campi?: {
    et_note?: string | null;
    et_imponib_1?: number;
    et_imposta_1?: number;
    et_totlordo?: number;
    et_totmerce?: number;
    xx_codpaga?: string;
    [k: string]: any;
  };
  RigheOrd?: {
    Collection?: ErpOrderRiga[];
    [k: string]: any;
  };
  [key: string]: any;
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const isEmpty = value === undefined || value === null || value === '' || value === '—';
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-secondary/60 last:border-b-0">
      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} ${isEmpty ? 'text-muted-foreground italic' : 'text-foreground'}`}>
        {isEmpty ? '—' : value}
      </span>
    </div>
  );
}

export default function AdminErpOrderDetailPage() {
  const { tipork, anno, serie, numdoc } = useParams<{ tipork: string; anno: string; serie: string; numdoc: string }>();
  const [order, setOrder] = useState<ErpOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!tipork || !anno || !serie || !numdoc) return;
    setLoading(true);
    setNotFound(false);
    try {
      const res = await api.get(`/api/erp/ordini/${encodeURIComponent(tipork)}/${anno}/${encodeURIComponent(serie)}/${numdoc}`);
      setOrder(res.data || null);
      if (!res.data) setNotFound(true);
    } catch (err: any) {
      if (err?.response?.status === 404) setNotFound(true);
      else toast.error('Errore caricamento ordine ERP');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [tipork, anno, serie, numdoc]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="admin-erp-order-detail-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[hsl(214,58%,41%)]" />
          <p className="text-sm text-muted-foreground">Caricamento ordine dall'ERP…</p>
          <p className="text-[11px] text-muted-foreground mt-1">Live call a NTS Business-API, può richiedere alcuni secondi.</p>
        </div>
      </div>
    );
  }

  if (notFound || !order || Object.keys(order).length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="admin-erp-order-detail-not-found">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h1 className="text-xl font-semibold mb-2">Ordine ERP non trovato</h1>
          <p className="text-sm text-muted-foreground mb-5 font-mono">
            {tipork}/{anno}/{serie}/{numdoc}
          </p>
          <Link
            to="/admin/ordini"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(206,54%,28%)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Torna agli ordini
          </Link>
        </div>
      </div>
    );
  }

  const aperto = order.et_aperto === 'Y';
  const confermato = order.et_confermato === 'Y';
  const righe: ErpOrderRiga[] = order.RigheOrd?.Collection || [];
  const campi = order.Campi || {};

  const conto = order.et_conto;
  const clienteDescr = order.an_descr1 || order._cliente?.an_descr1 || `Conto ${conto}`;
  const note = campi.et_note || (order as any).et_note || '';
  const pagamentoLabel = campi.xx_codpaga || '';
  // Importi IVA esclusa (INT-130): merce + spese d'incasso, come al Cliente.
  const merce = campi.et_totmerce;
  const spese = Number(campi.et_speinc || 0);
  const totale = merce != null ? Math.round((merce + spese) * 100) / 100 : null;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-erp-order-detail-page">
      <Link
        to="/admin/ordini"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        data-testid="admin-erp-order-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Torna agli ordini
      </Link>

      {/* Header */}
      <div className="rounded-xl border bg-white shadow-sm p-4 sm:p-5 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[11px] font-bold text-[hsl(214,58%,41%)] tracking-wide font-mono">
                {order.et_tipork}/{order.et_anno}/{order.et_serie}/{order.et_numdoc}
              </span>
              <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${aperto ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                {aperto ? 'Aperto' : 'Chiuso'}
              </span>
              {confermato && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  ✓ Confermato
                </span>
              )}
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                <Database className="w-3 h-3 inline-block mr-0.5" /> ERP NTS
              </span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight" data-testid="admin-erp-order-detail-title">
              Ordine {numeroGestionale(order.et_serie, order.et_anno, order.et_numdoc)}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span><Calendar className="w-3 h-3 inline-block mr-1" />{fmtDate(order.et_datdoc)}</span>
              <span>•</span>
              <span><strong className="text-foreground">{righe.length}</strong> righe</span>
              <span>•</span>
              <span><strong className="text-foreground">{fmt(order.et_totdoc)}</strong> IVA inclusa</span>
            </div>
          </div>

          <AggiornaDalGestionale
            numeroOrdine={numeroGestionale(order.et_serie, order.et_anno, order.et_numdoc)}
            onFatto={load}
            testId="admin-erp-order-detail-refresh"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Cliente / Conto */}
        <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
            <User className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Cliente</h2>
          </div>
          <div className="p-4">
            <Field label="Ragione sociale" value={clienteDescr} />
            <Field label="Conto ERP" value={conto} mono />
            <Field label="Pagamento" value={pagamentoLabel} />
          </div>
        </div>

        {/* Documento */}
        <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
            <Hash className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Documento</h2>
          </div>
          <div className="p-4">
            <Field label="Tipo ordine (tipork)" value={order.et_tipork} mono />
            <Field label="Serie" value={order.et_serie} mono />
            <Field label="Anno" value={order.et_anno} mono />
            <Field label="N. documento" value={order.et_numdoc} mono />
            <Field label="Data" value={fmtDate(order.et_datdoc)} />
          </div>
        </div>

        {/* Totali */}
        <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
            <Receipt className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Totali</h2>
          </div>
          <div className="p-4 space-y-2 text-sm">
            {merce != null && (
              <div className="flex justify-between"><span className="text-muted-foreground">Merce</span><span className="tabular-nums font-medium">{fmt(merce)}</span></div>
            )}
            {spese > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Spese d&apos;incasso</span><span className="tabular-nums font-medium">{fmt(spese)}</span></div>
            )}
            <div className="border-t pt-2 mt-1 flex justify-between"><span className="text-muted-foreground">Totale IVA esclusa</span><span className="tabular-nums font-medium">{totale != null ? fmt(totale) : '—'}</span></div>
            <div className="flex justify-between text-base">
              <span className="font-semibold">Totale <span className="text-xs font-normal text-muted-foreground">IVA inclusa</span></span>
              <span className="tabular-nums font-bold text-[hsl(214,58%,41%)]">{fmt(order.et_totdoc)}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t">
              <span>Stato</span>
              <span className="font-semibold">{aperto ? 'Aperto' : 'Chiuso'}{confermato ? ' · Confermato' : ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Righe */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden mb-5">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Articoli ({righe.length})</h2>
          </div>
        </div>
        {righe.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
            Nessuna riga articolo presente per questo ordine.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-erp-order-detail-items-table">
              <thead>
                <tr className="border-b bg-secondary/30">
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">N.</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">SKU</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Descrizione</th>
                  <th className="text-center px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Qtà</th>
                  <th className="text-center px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">UdM</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Prezzo</th>
                  <th className="text-center px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Sconto</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Totale</th>
                  <th className="text-center px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Evaso</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r, i) => {
                  const rcampi = r.Campi || {};
                  const sconto = (rcampi.ec_scont1 || 0) + (rcampi.ec_scont2 || 0) + (rcampi.ec_scont3 || 0);
                  return (
                    <tr key={i} className="border-b last:border-b-0 hover:bg-secondary/20 transition-colors" data-testid="admin-erp-order-detail-item-row">
                      <td className="px-3 py-2.5 text-xs text-muted-foreground tabular-nums">{r.ec_riga ?? i + 1}</td>
                      <td className="px-3 py-2.5 font-mono text-xs font-semibold text-[hsl(214,58%,41%)] whitespace-nowrap">
                        {r.ec_codart ? (
                          <Link to={`/admin/prodotti/${encodeURIComponent(r.ec_codart)}`} className="hover:underline">
                            {r.ec_codart}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-sm">{r.ec_descr || '—'}</td>
                      <td className="px-3 py-2.5 text-center font-semibold tabular-nums">{fmtNum(r.ec_quant, 0)}</td>
                      <td className="px-3 py-2.5 text-center text-xs text-muted-foreground">{r.ec_unmis || '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(r.ec_prezzo)}</td>
                      <td className="px-3 py-2.5 text-center">
                        {sconto > 0 ? (
                          <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                            -{fmtNum(sconto, 2)}%
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{fmt(r.ec_valore)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`text-xs font-semibold ${(r.ec_quaeva || 0) >= (r.ec_quant || 0) ? 'text-green-600' : 'text-amber-600'}`}>
                          {fmtNum(r.ec_quaeva || 0, 0)}/{fmtNum(r.ec_quant || 0, 0)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Note */}
      {note && (
        <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
            <FileText className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Note</h2>
          </div>
          <div className="p-4">
            <p className="text-sm whitespace-pre-wrap">{note}</p>
          </div>
        </div>
      )}
    </div>
  );
}
